import React, { useState, useRef } from "react";
import { useLanguage } from "@/context/LanguageContext";
import { api, adminApi, fileUrl } from "@/lib/api";
import {
  Plus,
  Trash2,
  Upload,
  X,
  Save,
  Loader2,
  Quote as QuoteIcon,
  User,
} from "lucide-react";

const emptyItem = {
  quote: "",
  author_name: "",
  author_role: "",
  image_url: null,
  sort_order: 0,
};

export default function AdminTestimonials({ adminKey }) {
  const { t } = useLanguage();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");
  const [initialized, setInitialized] = useState(false);
  const fileRef = useRef(null);

  const client = adminApi(adminKey);

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get("/testimonials");
      setItems(res.data || []);
    } finally {
      setLoading(false);
    }
  };

  if (!initialized) {
    setInitialized(true);
    load();
  }

  const startNew = () =>
    setEditing({ ...emptyItem, sort_order: items.length + 1 });
  const startEdit = (it) => setEditing({ ...it });
  const cancel = () => {
    setEditing(null);
    setError("");
  };

  const update = (k) => (e) =>
    setEditing({ ...editing, [k]: e.target.value });

  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    setError("");
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await client.post("/uploads/image", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setEditing((prev) => ({ ...prev, image_url: res.data.url }));
    } catch (err) {
      setError(err?.response?.data?.detail || "Upload misslyckades");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const save = async () => {
    setError("");
    try {
      const payload = {
        quote: editing.quote,
        author_name: editing.author_name,
        author_role: editing.author_role || null,
        image_url: editing.image_url || null,
        sort_order: Number(editing.sort_order) || 0,
      };
      if (editing.id) {
        await client.patch(`/testimonials/${editing.id}`, payload);
      } else {
        await client.post("/testimonials", payload);
      }
      setEditing(null);
      await load();
    } catch (err) {
      setError(err?.response?.data?.detail || "Kunde inte spara");
    }
  };

  const remove = async (it) => {
    if (!window.confirm(t.admin.testimonials.confirm_delete)) return;
    await client.delete(`/testimonials/${it.id}`);
    await load();
  };

  return (
    <div data-testid="admin-testimonials-panel">
      <div className="flex items-end justify-between flex-wrap gap-4 mb-6">
        <div>
          <h2 className="font-display text-3xl md:text-4xl uppercase">
            {t.admin.testimonials.title}
          </h2>
          <p className="mt-2 text-sm text-zinc-400 max-w-xl">
            {t.admin.testimonials.lead}
          </p>
        </div>
        <button
          onClick={startNew}
          data-testid="admin-testimonials-add-btn"
          className="inline-flex items-center gap-2 bg-[#ff5a00] text-black px-5 py-3 text-xs tracking-[0.2em] uppercase font-bold hover:bg-[#ff7a30] transition-colors"
        >
          <Plus size={14} /> {t.admin.testimonials.add}
        </button>
      </div>

      {loading ? (
        <div className="text-zinc-500 font-mono text-sm">{t.common.loading}</div>
      ) : items.length === 0 ? (
        <div className="border border-zinc-800 bg-[#0d0d10] p-12 text-center text-zinc-500">
          <QuoteIcon size={36} className="mx-auto mb-3 text-zinc-700" />
          {t.admin.testimonials.empty}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {items.map((it, i) => (
            <article
              key={it.id}
              data-testid={`admin-testimonial-row-${it.id}`}
              className="relative border border-zinc-800 bg-[#0d0d10] p-5 hover:bg-zinc-900/60 transition-colors"
            >
              <div className="flex items-center justify-between mb-3 text-[10px] font-mono tracking-widest uppercase text-zinc-500">
                <span>/ {String(i + 1).padStart(2, "0")}</span>
                <span>Ordning {it.sort_order}</span>
              </div>
              <blockquote className="text-zinc-200 text-sm leading-relaxed line-clamp-3">
                <span className="text-[#ff5a00] font-display text-xl leading-none align-top mr-1">
                  &ldquo;
                </span>
                {it.quote}
              </blockquote>
              <div className="mt-4 pt-4 border-t border-zinc-800 flex items-center gap-3">
                <div className="w-10 h-10 bg-zinc-900 border border-zinc-800 flex items-center justify-center overflow-hidden shrink-0">
                  {it.image_url ? (
                    <img
                      src={fileUrl(it.image_url)}
                      alt={it.author_name}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <User size={14} className="text-zinc-600" />
                  )}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="font-display text-base uppercase truncate">
                    {it.author_name}
                  </div>
                  {it.author_role && (
                    <div className="text-[10px] text-zinc-500 font-mono uppercase tracking-widest truncate">
                      {it.author_role}
                    </div>
                  )}
                </div>
                <div className="flex gap-2 shrink-0">
                  <button
                    onClick={() => startEdit(it)}
                    data-testid={`admin-testimonial-edit-${it.id}`}
                    className="border border-zinc-700 text-white px-3 py-1.5 text-[10px] tracking-widest uppercase hover:border-[#ff5a00] hover:text-[#ff5a00] transition-colors"
                  >
                    Redigera
                  </button>
                  <button
                    onClick={() => remove(it)}
                    data-testid={`admin-testimonial-delete-${it.id}`}
                    className="border border-zinc-700 text-zinc-500 px-2 py-1.5 hover:border-red-500 hover:text-red-400 transition-colors"
                    title={t.admin.delete}
                  >
                    <Trash2 size={12} />
                  </button>
                </div>
              </div>
            </article>
          ))}
        </div>
      )}

      {editing && (
        <div
          className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto"
          data-testid="admin-testimonial-modal"
        >
          <div className="bg-[#0d0d10] border border-zinc-800 max-w-2xl w-full my-8">
            <div className="flex items-center justify-between p-6 border-b border-zinc-800">
              <div className="font-display text-2xl uppercase">
                {editing.id ? "Redigera citat" : t.admin.testimonials.add}
              </div>
              <button onClick={cancel} className="text-zinc-500 hover:text-white">
                <X size={20} />
              </button>
            </div>

            <div className="p-6 grid grid-cols-1 gap-5">
              <div>
                <label className="text-xs font-mono tracking-[0.3em] uppercase text-zinc-500 block">
                  {t.admin.testimonials.fields.quote} *
                </label>
                <textarea
                  data-testid="admin-testimonial-quote"
                  className="brut-input"
                  value={editing.quote}
                  onChange={update("quote")}
                  rows={5}
                  placeholder="Skriv citatet här..."
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div>
                  <label className="text-xs font-mono tracking-[0.3em] uppercase text-zinc-500 block">
                    {t.admin.testimonials.fields.author_name} *
                  </label>
                  <input
                    type="text"
                    data-testid="admin-testimonial-author"
                    className="brut-input"
                    value={editing.author_name}
                    onChange={update("author_name")}
                    placeholder="Anna Andersson"
                  />
                </div>
                <div>
                  <label className="text-xs font-mono tracking-[0.3em] uppercase text-zinc-500 block">
                    {t.admin.testimonials.fields.author_role}
                  </label>
                  <input
                    type="text"
                    data-testid="admin-testimonial-role"
                    className="brut-input"
                    value={editing.author_role || ""}
                    onChange={update("author_role")}
                    placeholder="VD, Företag AB"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                <div className="md:col-span-2">
                  <label className="text-xs font-mono tracking-[0.3em] uppercase text-zinc-500">
                    {t.admin.testimonials.upload_image}
                  </label>
                  <div className="mt-3 flex gap-4 items-center">
                    <div className="w-16 h-16 bg-zinc-900 border border-zinc-800 flex items-center justify-center overflow-hidden shrink-0">
                      {editing.image_url ? (
                        <img
                          src={fileUrl(editing.image_url)}
                          alt="preview"
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <User size={20} className="text-zinc-700" />
                      )}
                    </div>
                    <div className="flex-1">
                      <input
                        type="file"
                        ref={fileRef}
                        onChange={onUpload}
                        accept="image/png,image/jpeg,image/webp,image/gif"
                        className="hidden"
                        data-testid="admin-testimonial-file-input"
                      />
                      <button
                        type="button"
                        onClick={() => fileRef.current?.click()}
                        disabled={uploading}
                        data-testid="admin-testimonial-upload-btn"
                        className="inline-flex items-center gap-2 border border-zinc-700 text-white px-4 py-2 text-xs tracking-widest uppercase hover:border-[#ff5a00] hover:text-[#ff5a00] disabled:opacity-50"
                      >
                        {uploading ? (
                          <Loader2 size={14} className="animate-spin" />
                        ) : (
                          <Upload size={14} />
                        )}
                        {uploading ? "Laddar upp..." : "Bild"}
                      </button>
                      {editing.image_url && (
                        <button
                          type="button"
                          onClick={() =>
                            setEditing({ ...editing, image_url: null })
                          }
                          className="ml-2 text-xs text-zinc-500 hover:text-red-400"
                        >
                          {t.admin.testimonials.remove_image}
                        </button>
                      )}
                    </div>
                  </div>
                </div>
                <div>
                  <label className="text-xs font-mono tracking-[0.3em] uppercase text-zinc-500 block">
                    {t.admin.testimonials.fields.sort_order}
                  </label>
                  <input
                    type="number"
                    data-testid="admin-testimonial-order"
                    className="brut-input"
                    value={editing.sort_order}
                    onChange={update("sort_order")}
                  />
                </div>
              </div>
            </div>

            {error && (
              <div className="px-6 pb-2 text-sm text-red-400">{error}</div>
            )}

            <div className="p-6 border-t border-zinc-800 flex gap-3 justify-end">
              <button
                onClick={cancel}
                className="border border-zinc-700 text-white px-5 py-3 text-xs tracking-widest uppercase hover:border-zinc-500"
              >
                {t.admin.testimonials.cancel}
              </button>
              <button
                onClick={save}
                data-testid="admin-testimonial-save-btn"
                disabled={!editing.quote || !editing.author_name}
                className="inline-flex items-center gap-2 bg-[#ff5a00] text-black px-5 py-3 text-xs tracking-widest uppercase font-bold hover:bg-[#ff7a30] disabled:opacity-50"
              >
                <Save size={14} /> {t.admin.testimonials.save}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
